import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a checkpoint element in the training track
 */
export class CheckpointElement extends TrackElement {
    private checkpointMesh: THREE.Mesh | null = null;
    private textMesh: THREE.Mesh | null = null;
    private active: boolean = false;
    private checkpointNumber: number;
    
    /**
     * Constructor
     * @param element The checkpoint element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
        this.checkpointNumber = element.properties?.checkpointNumber || 0;
    }
    
    /**
     * Creates the Three.js object for this checkpoint
     * @returns The created checkpoint object
     */
    protected createObject(): THREE.Object3D {
        // Create a cylinder for the checkpoint
        const geometry = new THREE.CylinderGeometry(
            this.properties.triggerRadius || 3,
            this.properties.triggerRadius || 3,
            TRAINING_TRACK_CONFIG.CHECKPOINT.HEIGHT,
            16,
            1,
            true // Open-ended cylinder
        );
        
        const material = new THREE.MeshBasicMaterial({
            color: TRAINING_TRACK_CONFIG.CHECKPOINT.COLOR,
            transparent: true,
            opacity: TRAINING_TRACK_CONFIG.CHECKPOINT.OPACITY,
            side: THREE.DoubleSide
        });
        
        this.checkpointMesh = new THREE.Mesh(geometry, material);
        this.checkpointMesh.userData.active = false; // Initially inactive
        
        // Create text for checkpoint number
        const textGeometry = new THREE.PlaneGeometry(1, 1);
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        if (context) {
            context.fillStyle = 'white';
            context.font = 'Bold 80px Arial';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(this.checkpointNumber.toString(), 64, 64);
        }
        
        const textTexture = new THREE.CanvasTexture(canvas);
        const textMaterial = new THREE.MeshBasicMaterial({
            map: textTexture,
            transparent: true,
            side: THREE.DoubleSide
        });
        
        this.textMesh = new THREE.Mesh(textGeometry, textMaterial);
        this.textMesh.position.y = 1; // Position above the ground
        this.textMesh.scale.set(2, 2, 2); // Scale to appropriate size
        
        // Create a group to hold both the checkpoint and its number
        const group = new THREE.Group();
        group.add(this.checkpointMesh);
        group.add(this.textMesh);
        
        return group;
    }
    
    /**
     * Updates the checkpoint animation
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        if (this.checkpointMesh && this.active) {
            // Pulse effect for active checkpoints
            const scale = 1 + 0.2 * Math.sin(Date.now() * TRAINING_TRACK_CONFIG.CHECKPOINT.PULSE_SPEED);
            this.checkpointMesh.scale.set(scale, 1, scale);
            
            // Rotate slowly
            this.checkpointMesh.rotation.y += 0.01;
        }
    }
    
    /**
     * Handles player interaction with this checkpoint
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        // Add debug logging for checkpoint interaction
        console.log(`[Checkpoint] Checking interaction with checkpoint ${this.id}:`, {
            distance: distance.toFixed(2),
            triggerRadius: this.properties.triggerRadius,
            isActive: this.properties.active,
            checkpointNumber: this.properties.checkpointNumber,
            playerPosition: playerPosition,
            checkpointPosition: this.object?.position
        });

        if (!this.properties.active) {
            console.log(`[Checkpoint] ${this.id} is not active, skipping interaction`);
            return false;
        }

        if (distance <= this.properties.triggerRadius) {
            console.log(`[Checkpoint] ${this.id} triggered! Distance: ${distance.toFixed(2)} <= ${this.properties.triggerRadius}`);
            return true;
        }

        return false;
    }
    
    /**
     * Sets the checkpoint as active or inactive
     * @param active Whether the checkpoint should be active
     */
    public setActive(active: boolean): void {
        this.active = active;
        
        if (this.checkpointMesh) {
            this.checkpointMesh.userData.active = active;
            
            // Update color based on active state
            if (active) {
                (this.checkpointMesh.material as THREE.MeshBasicMaterial).color.setHex(TRAINING_TRACK_CONFIG.CHECKPOINT.ACTIVE_COLOR);
            } else {
                (this.checkpointMesh.material as THREE.MeshBasicMaterial).color.setHex(TRAINING_TRACK_CONFIG.CHECKPOINT.COMPLETED_COLOR);
            }
        }
    }
    
    /**
     * Checks if the checkpoint is active
     * @returns True if the checkpoint is active, false otherwise
     */
    public isActive(): boolean {
        return this.active;
    }
    
    /**
     * Gets the checkpoint number
     * @returns The checkpoint number
     */
    public getCheckpointNumber(): number {
        return this.checkpointNumber;
    }
}